import { useState } from 'react';
import fLogger from '@fintronners/react-utils/src/fLogger';
import { V1RecurringRuleInterval } from '@fintronners/react-api/src';
import { TransferType, DepositType } from '@fintronners/internal-core/src/transfer/constants';
import { TransferFormData } from '@fintronners/internal-core/src/transfer/schema';
import { UseExternalAccount } from '@fintronners/react-widgets/src/hooks/useExternalAccounts';

/**
 * Custom hook for handling transfer submission logic
 *
 * Manages the submission of deposits, withdrawals, and recurring deposits, including:
 * - Building transfer request payloads
 * - Handling deposit/withdrawal logic
 * - Managing submission loading state
 * - Error handling and success/failure callbacks
 *
 * @param params - Submission parameters including transfer functions and callbacks
 * @returns Transfer submission function and loading state
 */
type UseTransferSubmissionParams = {
  firstExternalAccount?: UseExternalAccount | null;
  createTransfer: (params: {
    amount: string;
    account?: UseExternalAccount | null;
    taxYear?: number;
  }) => Promise<unknown>;
  createRecurringDeposit: (params: {
    amount: string;
    recurringInterval?: V1RecurringRuleInterval;
    selectedDate?: string;
    account?: UseExternalAccount;
  }) => Promise<unknown>;
  createWithdrawal: (params: { amount: string; accountId?: string }) => Promise<unknown>;
  onSuccess: () => void;
  onError: () => void;
  onLoadingChange?: (loading: boolean) => void;
};

export const useTransferSubmission = ({
  firstExternalAccount,
  createTransfer,
  createRecurringDeposit,
  createWithdrawal,
  onSuccess,
  onError,
  onLoadingChange,
}: UseTransferSubmissionParams) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const performTransfer = async (values: TransferFormData) => {
    const { amount, interval, transferType, depositType, selectedStartDate, taxYear } = values;

    setIsSubmitting(true);
    onLoadingChange?.(true);

    try {
      // Handle withdrawals
      if (transferType === TransferType.WITHDRAWAL) {
        await createWithdrawal({
          amount,
          accountId: firstExternalAccount?.id,
        });
        onSuccess();
        return;
      }

      // Handle one-time deposits
      if (depositType === DepositType.ONE_TIME) {
        await createTransfer({
          amount,
          account: firstExternalAccount,
          taxYear,
        });

        onSuccess();
        return;
      }

      // Handle recurring deposits
      if (depositType === DepositType.RECURRING_DEPOSIT) {
        await createRecurringDeposit({
          amount,
          recurringInterval: interval,
          selectedDate: selectedStartDate,
          account: firstExternalAccount ?? undefined,
        });

        onSuccess();
        return;
      }
    } catch (error) {
      fLogger.error(`Error creating ${depositType || transferType} transfer`, error);
      onError();
    } finally {
      setIsSubmitting(false);
      onLoadingChange?.(false);
    }
  };

  return {
    performTransfer,
    isSubmitting,
  };
};

export default useTransferSubmission;
